import { useTranslation } from 'react-i18next';
import type { FinancingLenderView } from '@hero/api';

export type LenderViewHeroProps = {
  /**
   * Lender slice from {@link FinancingLenderView}. Always present
   * after the single-call query resolves — there is no separate
   * partners-list fetch.
   */
  lender: FinancingLenderView['lender'];
  /** Carrier company name, always present (not masked). */
  carrierName: string;
  /** Stable reference for the application (e.g. `FIN-2026-00042`). */
  refValue: string;
};

/**
 * Co-branded banner at the top of the public lender-view page.
 *
 * Reads the lender display info straight off the single-shot
 * {@link FinancingLenderView} payload — there's no separate "active
 * lenders" list fetch like the legacy partner page used. TanStack
 * Query persists the parent response via `PersistQueryClientProvider`,
 * so the Tarmeez-style branding stays put on a hard refresh.
 */
export function LenderViewHero({
  lender,
  carrierName,
  refValue,
}: LenderViewHeroProps) {
  const { t } = useTranslation('m-financing');
  const brandColor = lender.brandColor ?? '#0F766E';

  return (
    <header className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-5 p-5 sm:p-6">
        {lender.logoUrl ? (
          <img
            src={lender.logoUrl}
            alt={lender.name}
            className="size-12 shrink-0 rounded-xl object-contain shadow-sm sm:size-14"
          />
        ) : (
          <span
            aria-hidden
            className="flex size-12 shrink-0 items-center justify-center rounded-xl text-lg font-semibold text-white shadow-sm sm:size-14"
            style={{ backgroundColor: brandColor }}>
            {lender.name.trim().charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-lg font-semibold text-gray-900 sm:text-xl">
            {t('partner_page.welcome', { partner: lender.name })}
          </h1>
          <p className="mt-0.5 text-sm leading-relaxed text-gray-600">
            {lender.tagline ?? t('partner_page.subhead')}
          </p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-gray-50/60 px-3 py-2 text-end">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            {t('partner_page.fields.ref')}
          </div>
          <div className="mt-1 font-mono text-sm text-gray-900">{refValue}</div>
          <div className="mt-2 text-[11px] text-gray-500">{carrierName}</div>
        </div>
      </div>

      <div
        aria-hidden
        className="h-1 w-full"
        style={{ backgroundColor: brandColor }}
      />
    </header>
  );
}
